import { listMyTrainingSessions, listTeamTrainingSessions } from "@/api";
import {
  DataTableSource,
  type DataTablePageOptions,
  type DataTablePageReq,
  type DataTablePageResp,
} from "@carbon6/c6-frontend-design";
import {
  SubmitSessionTableRow,
  type SubmitSessionRowActionBuilder,
  type SubmitSessionRowClickHandler,
} from "./SubmitSessionTableRow";
import {
  EMPTY_SESSION_FILTERS,
  sessionFilterSignature,
  sessionFiltersToQuery,
  type SessionFilterState,
} from "./sessionFilters";

// 비교 선택 상한. 한 좌표축에 겹쳐 그린 곡선을 사람이 눈으로 따라갈 수 있는 수다. 색은 더
// 있다(공용 팔레트는 15색). 그러나 선이 서로를 가리는 한계가 색 수보다 먼저 온다.
export const MAX_SESSION_COMPARE_SELECTION = 6;

// source scope. 생성자에서 확정한다. me/team kind 는 나중에 바꿀 수 없으므로 스코프를 바꾸면
// 호출부가 패널을 다시 마운트한다. team 은 페이지가 고른 groupUuid 로 스코프를 정하고, setQuery
// 로 그룹과 필터를 함께 바꾼다. 생성자가 최초 scope 를 받으므로 team 패널이 잠시 개인 API 를
// 호출하는 상태가 없다.
export type SubmitSessionScope = { kind: "me" } | { kind: "team"; groupUuid: string | null };

type SubmitSessionTableSourceOptions = {
  scope: SubmitSessionScope;
  pageSize: number;
  rowClickHandler: SubmitSessionRowClickHandler;
  // 행 ⋯ 메뉴(복제·이어서 학습·재학습). 지정하지 않으면 액션 열을 표시하지 않는다.
  rowActionsBuilder?: SubmitSessionRowActionBuilder;
  resolveErrorMessage: (error: unknown) => string;
  // URL 딥링크의 첫 요청부터 필터를 적용한다. 필터 없는 조회 뒤 필터 조회를 다시 하지 않는다.
  initialFilters?: SessionFilterState;
};

// 세션(내/팀) 데이터테이블 source. BE 가 서버 페이징(PageResp)을 하므로 페이지마다 scope 에 맞는
// 목록 API 를 직접 호출한다. me 는 listMyTrainingSessions, team 은 listTeamTrainingSessions 다.
// team 에서 그룹이 null 이면 요청 없이 빈 목록을 반환한다. status 는 서버 필터(쿼리)이고, 바뀌면
// 1페이지부터 다시 받는다.
export class SubmitSessionTableSource extends DataTableSource<SubmitSessionTableRow> {
  private readonly rowClickHandler: SubmitSessionRowClickHandler;
  private readonly rowActionsBuilder: SubmitSessionRowActionBuilder | null;
  private readonly scopeKind: "me" | "team";
  private groupUuid: string | null;
  // 모든 조회 축(멤버·실험·상태·텍스트·기간). URL 이 소유하는 값을 패널이 그대로 전달한다.
  // source 는 그 값을 페이지 요청으로 옮기기만 한다.
  private filters: SessionFilterState = EMPTY_SESSION_FILTERS;

  constructor({
    scope,
    pageSize,
    rowClickHandler,
    rowActionsBuilder,
    resolveErrorMessage,
    initialFilters = EMPTY_SESSION_FILTERS,
  }: SubmitSessionTableSourceOptions) {
    super({ initialPageSize: pageSize, resolveErrorMessage, prefetchMode: "paging" });
    this.rowClickHandler = rowClickHandler;
    this.rowActionsBuilder = rowActionsBuilder ?? null;
    this.scopeKind = scope.kind;
    this.groupUuid = scope.kind === "team" ? scope.groupUuid : null;
    this.filters = initialFilters;
  }

  // 그룹과 URL 필터를 한 번에 적용한다. 그룹 변경 커밋은 member/experiment 도 함께 지운다.
  // setter 를 둘로 나누면 같은 렌더에서 1페이지 요청이 두 번 나간다.
  setQuery(groupUuid: string | null, filters: SessionFilterState): Promise<void> {
    const nextGroupUuid = this.scopeKind === "team" ? groupUuid : null;
    const groupChanged = nextGroupUuid !== this.groupUuid;
    const filtersChanged = sessionFilterSignature(filters) !== sessionFilterSignature(this.filters);
    if (!groupChanged && !filtersChanged) {
      return Promise.resolve();
    }
    if (groupChanged || filters.memberUuid !== this.filters.memberUuid) {
      this.clearSelection();
    }
    this.groupUuid = nextGroupUuid;
    this.filters = filters;
    return this.goToPage(1);
  }

  // 개별 setter 는 source 단위 호출부를 위한 편의 경계다. 실제 상태 전이는 setQuery 하나가 맡는다.
  setGroup(groupUuid: string | null): Promise<void> {
    return this.setQuery(groupUuid, this.filters);
  }

  // 선택 상한. 행 자격(canSelect)과 별개다. 상한에 닿으면 나머지 행 체크박스가 disabled 로 보인다.
  protected override canSelectMore(): boolean {
    return this.selectedRowCount < MAX_SESSION_COMPARE_SELECTION;
  }

  // 필터가 바뀌면 1페이지부터 다시 받는다. 멤버가 바뀌면 데이터 집합이 달라지므로 선택을 비운다.
  // 나머지 필터는 바꿔 가며 비교 대상을 모을 수 있도록 선택을 유지한다.
  setFilters(filters: SessionFilterState): Promise<void> {
    return this.setQuery(this.groupUuid, filters);
  }

  // scope discriminator 를 캐시 키에 넣는다. 그래서 me/team·그룹·필터 조합마다 페이지 캐시가 다르다.
  protected override cacheSignature(): string {
    const filters = sessionFilterSignature(this.filters);
    return this.scopeKind === "me" ? `me|${filters}` : `team|${this.groupUuid ?? ""}|${filters}`;
  }

  protected override async onPage(
    req: DataTablePageReq,
    { signal }: DataTablePageOptions,
  ): Promise<DataTablePageResp<SubmitSessionTableRow>> {
    const filters = sessionFiltersToQuery(this.filters);

    if (this.scopeKind === "team") {
      // 그룹 미선택(소속 그룹 없음/로딩 전). 화면 스코프가 없으므로 요청 없이 빈 목록을 반환한다.
      if (this.groupUuid === null) {
        return { rows: [], total: 0, page: req.page, size: req.size };
      }
      const page = await listTeamTrainingSessions(
        this.groupUuid,
        {
          page: req.page,
          size: req.size,
          ...filters,
        },
        this.filters.memberUuid ?? undefined,
        { signal },
      );
      return {
        rows: page.items.map(
          (view) => new SubmitSessionTableRow(view, this.rowClickHandler, this.rowActionsBuilder),
        ),
        total: page.total,
        page: page.page,
        size: page.size,
      };
    }

    const page = await listMyTrainingSessions(
      { page: req.page, size: req.size, ...filters },
      { signal },
    );
    return {
      rows: page.items.map(
        (view) => new SubmitSessionTableRow(view, this.rowClickHandler, this.rowActionsBuilder),
      ),
      total: page.total,
      page: page.page,
      size: page.size,
    };
  }
}
